/**
 * TD Flavour Variant Picker Component
 * Handles flavour selection with variety pack support.
 * When the product media gallery has variant-specific images (custom.variant_specific_images),
 * replaces the gallery with only that variant's images on selection; Variety Pack shows full gallery.
 */

if (!customElements.get('td-flavour-picker')) {
  class TdFlavourPicker extends HTMLElement {
    constructor() {
      super();
      // State only - no DOM access; elements are not guaranteed to exist in constructor
      this.currentQuantity = 1;
      this.hideTimeout = null;
      this.isInteracting = false;

      /** @type {string | null} Saved full gallery HTML for restoring when Variety Pack is selected */
      this.fullGalleryHtml = null;

      // Store bound function references for cleanup
      this.boundHandleChange = this.handleChange.bind(this);
      this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
      this.boundHandleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.boundHandleMouseEnter = () => { this.isInteracting = true; };
      this.boundHandleMouseLeave = () => { this.isInteracting = false; };
      this.boundHandleFocus = () => { this.isInteracting = true; };
      this.boundHandleBlur = () => { this.isInteracting = false; };
    }

    connectedCallback() {
      // DOM refs only in connectedCallback - element is in the document with children
      this.inputs = this.querySelectorAll('.td-flavour-picker__input');
      this.options = this.querySelectorAll('.td-flavour-picker__option');
      this.selectedValueDisplay = this.querySelector('[data-selected-value]');
      this.varietyPackOption = this.querySelector('[data-variety-pack-option]');
      this.varietyPackInput = this.querySelector('[data-is-variety-pack="true"]');
      this.varietyPackSubtext = this.querySelector('[data-variety-pack-subtext]');

      this.inputs.forEach(input => {
        input.addEventListener('change', this.boundHandleChange);
      });

      // Track interaction state for variety pack option
      if (this.varietyPackOption && this.varietyPackInput) {
        this.varietyPackOption.addEventListener('mouseenter', this.boundHandleMouseEnter);
        this.varietyPackOption.addEventListener('mouseleave', this.boundHandleMouseLeave);
        this.varietyPackInput.addEventListener('focus', this.boundHandleFocus);
        this.varietyPackInput.addEventListener('blur', this.boundHandleBlur);
      }

      // Listen for size/quantity changes from product form
      document.addEventListener('td-size-change', this.boundHandleQuantityChange);

      // When the section re-renders (variant:update), the gallery may be replaced; invalidate saved HTML
      const section = this.closest('.shopify-section');
      if (section) {
        this._variantUpdateSection = section;
        section.addEventListener('variant:update', this.boundHandleVariantUpdate);
      }

      // Check initial quantity state from currently selected size card
      this.initializeQuantityState();

      // Save the full gallery HTML for variety pack restore (no swap needed — Liquid renders the right gallery)
      this.applyVariantGalleryOnLoad();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    /**
     * Cleans up timeouts, stored HTML, and event listeners when the component is disconnected.
     * Freeing fullGalleryHtml (cloned gallery HTML) here avoids retaining a large string in memory.
     */
    cleanup() {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }

      this.fullGalleryHtml = null;

      if (this.inputs) {
        this.inputs.forEach((input) => {
          input.removeEventListener('change', this.boundHandleChange);
        });
      }

      if (this.varietyPackOption) {
        this.varietyPackOption.removeEventListener('mouseenter', this.boundHandleMouseEnter);
        this.varietyPackOption.removeEventListener('mouseleave', this.boundHandleMouseLeave);
      }

      if (this.varietyPackInput) {
        this.varietyPackInput.removeEventListener('focus', this.boundHandleFocus);
        this.varietyPackInput.removeEventListener('blur', this.boundHandleBlur);
      }

      document.removeEventListener('td-size-change', this.boundHandleQuantityChange);

      if (this._variantUpdateSection) {
        this._variantUpdateSection.removeEventListener('variant:update', this.boundHandleVariantUpdate);
        this._variantUpdateSection = null;
      }
    }

    /**
     * Invalidate saved full gallery HTML when the section re-renders (e.g. variant:update replaces
     * the media-gallery). Prevents restoring stale HTML into a new gallery element.
     */
    handleVariantUpdate() {
      this.fullGalleryHtml = null;
    }

    /** @returns {HTMLElement | null} Media gallery in the same section (TD product media gallery) */
    getMediaGallery() {
      return this.closest('.shopify-section')?.querySelector('media-gallery') ?? null;
    }

    /**
     * Saves the full gallery HTML for later restore (Variety Pack).
     * Checks for a Liquid-rendered <template id="td-gallery-full-{blockId}"> first (used when
     * the initial gallery was rendered with variant-specific images). Falls back to cloning the
     * current slideshow + zoom-dialog from the live DOM.
     * Only saves once — the full gallery is static and never changes.
     * @param {HTMLElement} gallery
     */
    saveFullGalleryHtml(gallery) {
      if (this.fullGalleryHtml != null) return;

      // Try the Liquid-rendered full gallery template first
      const blockId = gallery?.dataset?.blockId;
      if (blockId) {
        const fullTemplate = gallery.querySelector(`template#td-gallery-full-${blockId}`);
        if (fullTemplate) {
          const frag = fullTemplate.content.cloneNode(true);
          const div = document.createElement('div');
          div.appendChild(frag);
          this.fullGalleryHtml = div.innerHTML;
          return;
        }
      }

      // Fallback: clone from current DOM (when the full gallery is the visible content)
      const slideshow = gallery?.querySelector('slideshow-component');
      const zoomDialog = gallery?.querySelector('zoom-dialog');
      if (!slideshow) return;
      const div = document.createElement('div');
      div.appendChild(slideshow.cloneNode(true));
      if (zoomDialog) div.appendChild(zoomDialog.cloneNode(true));
      this.fullGalleryHtml = div.innerHTML;
    }

    /**
     * Returns cloned content of the variant gallery template, or null if none.
     * Templates are rendered by Liquid (snippet td-gallery-variant-content) inside the gallery.
     * @param {HTMLElement} gallery
     * @param {string} variantId
     * @returns {DocumentFragment | null}
     */
    getVariantGalleryTemplateContent(gallery, variantId) {
      const template = gallery?.querySelector(`template#td-gallery-variant-${variantId}`);
      return template?.content?.cloneNode(true) ?? null;
    }

    /**
     * Replaces gallery content with full gallery (saved) or variant gallery (from template).
     * @param {HTMLElement | null} gallery
     * @param {'full' | { variantId: string }} mode
     */
    setGalleryContent(gallery, mode) {
      if (!gallery) return;
      const currentSlideshow = gallery.querySelector('slideshow-component');
      const currentZoom = gallery.querySelector('zoom-dialog');

      if (mode === 'full') {
        if (!this.fullGalleryHtml) return;
        if (currentSlideshow) currentSlideshow.remove();
        if (currentZoom) currentZoom.remove();
        const wrap = document.createElement('div');
        wrap.innerHTML = this.fullGalleryHtml;
        while (wrap.firstChild) gallery.appendChild(wrap.firstChild);
        return;
      }

      const content = this.getVariantGalleryTemplateContent(gallery, mode.variantId);
      if (!content) {
        this.setGalleryContent(gallery, 'full');
        return;
      }
      this.saveFullGalleryHtml(gallery);
      if (currentSlideshow) currentSlideshow.remove();
      if (currentZoom) currentZoom.remove();
      gallery.appendChild(content);
    }

    /**
     * On load: save the full gallery HTML so it can be restored when Variety Pack is selected.
     * The correct initial gallery is already rendered by Liquid (either variant-specific images
     * or the full gallery), so no JS swap is needed on page load.
     */
    applyVariantGalleryOnLoad() {
      const gallery = this.getMediaGallery();
      if (!gallery) return;
      this.saveFullGalleryHtml(gallery);
    }

    /**
     * After flavour change: update media gallery to show variant images or full gallery.
     * @param {boolean} isVarietyPack
     * @param {string | null} variantId
     */
    updateMediaGalleryForFlavour(isVarietyPack, variantId) {
      const gallery = this.getMediaGallery();
      if (!gallery) return;

      if (isVarietyPack) {
        this.setGalleryContent(gallery, 'full');
        return;
      }
      if (variantId && this.getVariantGalleryTemplateContent(gallery, variantId)) {
        this.setGalleryContent(gallery, { variantId });
      } else {
        this.setGalleryContent(gallery, 'full');
      }
    }

    initializeQuantityState() {
      // Find the currently selected size card and get its quantity
      const selectedSizeCard = document.querySelector('.td-size--selected');
      if (selectedSizeCard) {
        const quantity = parseInt(selectedSizeCard.dataset.quantity, 10) || 1;
        this.currentQuantity = quantity;
        this.updateVarietyPackVisibility();
      } else {
        // If no size is selected yet, wait a frame and try again
        requestAnimationFrame(() => {
          const selectedSizeCard = document.querySelector('.td-size--selected');
          if (selectedSizeCard) {
            const quantity = parseInt(selectedSizeCard.dataset.quantity, 10) || 1;
            this.currentQuantity = quantity;
            this.updateVarietyPackVisibility();
          }
        });
      }
    }

    handleQuantityChange(event) {
      const { quantity } = event.detail;
      this.currentQuantity = quantity;
      this.updateVarietyPackVisibility();
      // Update subtext if variety pack is visible
      if (this.varietyPackOption && this.varietyPackOption.style.display !== 'none') {
        this.updateVarietyPackSubtext();
      }
    }

    updateVarietyPackVisibility() {
      if (!this.varietyPackOption) return;

      // Clear any pending hide timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }

      // Show variety pack only when quantity > 1 AND quantity is even
      const shouldShow = this.currentQuantity > 1 && this.currentQuantity % 2 === 0;
      
      if (shouldShow) {
        // Show immediately
        this.varietyPackOption.style.display = '';
        this.updateVarietyPackSubtext();
      } else {
        // Debounce hiding to prevent race conditions during user interaction
        this.hideTimeout = setTimeout(() => {
          // Double-check that we still shouldn't show and user isn't interacting
          const stillShouldHide = !(this.currentQuantity > 1 && this.currentQuantity % 2 === 0);
          
          if (stillShouldHide && !this.isInteracting) {
            this.varietyPackOption.style.display = 'none';
            
            // If variety pack was selected, deselect it and select the first regular option
            if (this.varietyPackInput && this.varietyPackInput.checked) {
              const firstRegularInput = this.querySelector('.td-flavour-picker__input:not([data-is-variety-pack])');
              if (firstRegularInput) {
                firstRegularInput.checked = true;
                firstRegularInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
          
          this.hideTimeout = null;
        }, 150); // 150ms delay to allow interactions to complete
      }
    }

    updateVarietyPackSubtext() {
      if (!this.varietyPackSubtext) return;
      
      // Calculate split: each variant gets half the quantity
      const splitQuantity = this.currentQuantity / 2;
      
      // Update subtext: "X + X SPLIT"
      this.varietyPackSubtext.textContent = `${splitQuantity} + ${splitQuantity} SPLIT`;
    }

    handleChange(event) {
      const input = event.target;
      const selectedValue = input.value;
      const isVarietyPack = input.dataset.isVarietyPack === 'true';

      // Update selected class on options
      this.options.forEach(option => {
        option.classList.remove('td-flavour-picker__option--selected');
      });
      input.closest('.td-flavour-picker__option').classList.add('td-flavour-picker__option--selected');

      // Update heading display
      if (this.selectedValueDisplay) {
        this.selectedValueDisplay.textContent = selectedValue;
      }

      // Dispatch custom event for variant picker integration (product form, price, etc.)
      if (isVarietyPack) {
        const variantIds = input.dataset.varietyVariantIds?.split(',') || [];
        this.dispatchEvent(new CustomEvent('td-flavour-change', {
          bubbles: true,
          detail: {
            value: selectedValue,
            isVarietyPack: true,
            variantIds: variantIds,
            variantId: variantIds[0]
          }
        }));
        this.updateMediaGalleryForFlavour(true, null);
      } else {
        this.dispatchEvent(new CustomEvent('td-flavour-change', {
          bubbles: true,
          detail: {
            value: selectedValue,
            variantId: input.dataset.variantId,
            optionValueId: input.dataset.optionValueId,
            isVarietyPack: false
          }
        }));
        this.updateMediaGalleryForFlavour(false, input.dataset.variantId || null);
      }
    }
  }

  customElements.define('td-flavour-picker', TdFlavourPicker);
}
